import { Apple, ArrowLeft, ArrowRight, Check, Compass, Eye, EyeOff, LockKeyhole, Mail, UserRound } from "lucide-react";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { BrandSplash } from "../components/BrandSplash";
import { Logo } from "../components/Logo";
import { Spinner } from "../components/Spinner";
import { apiRequest, describeApiError } from "../lib/api";
import { useGallery } from "../lib/queries";
import type { ApiEnvelope, AuthSettings, OAuthProvider, OAuthProviderId } from "../types";
import { ButtonLabel } from "../components/ButtonLabel";
import { guestPathFrom, returnPathFrom, safeReturnPath } from "../lib/return-path";
import { SIMPLIFIED_PRIVACY_NOTICE } from "@aventuria/client-core/legal";

const DEFAULT_COPY: AuthSettings = {
  loginHeadline: "Tu viaje, finalmente al alcance.",
  loginPromise: "Ahorra, planea y financia tu próximo destino desde un solo lugar.",
  signupHeadline: "Empieza a ahorrar para tu aventura.",
  signupPromise: "Crea tu cuenta en minutos y elige cómo quieres llegar a tu próximo destino.",
  footer: "Aventuria es una agencia de viajes (RNT 0415057c6d248)."
};

export function FormError({ messages }: { messages: string[] | null }) {
  if (!messages?.length) return null;
  return (
    <div className="form-error" role="alert">
      {messages.length === 1 ? messages[0] : <ul className="form-error-list">{messages.map((message) => <li key={message}>{message}</li>)}</ul>}
    </div>
  );
}

interface LayoutProps {
  children: ReactNode;
  kicker: string;
  headline: string;
  promise: string;
  tab?: "login" | "register";
}

/**
 * Split screen from the "Desktop · Auth & loading" design: brand panel on the left — the
 * destination gallery when the administrator has published photos, the brand gradient otherwise —
 * and the form column on the right. On phones the panel collapses into a gradient header.
 */
export function AuthLayout({ children, kicker, headline, promise, tab }: LayoutProps) {
  const location = useLocation();
  const gallery = useGallery();
  const photos = gallery.data?.photos ?? [];
  const footer = gallery.data?.auth?.footer ?? DEFAULT_COPY.footer;
  return (
    <main className="auth-page">
      <section className={`auth-brand-panel${photos.length ? " gallery" : ""}`} aria-label="Aventuria">
        {photos.length > 0 && (
          <div className="auth-gallery" aria-hidden="true">
            {photos.slice(0, 6).map((photo, index) => (
              <div
                key={photo.id}
                className="auth-gallery-slide"
                style={{
                  backgroundImage: `url(${photo.url})`,
                  animationDuration: `${photos.slice(0, 6).length * 4}s`,
                  animationDelay: `${index * 4}s`
                }}
              />
            ))}
            <div className="auth-gallery-shade" />
          </div>
        )}
        <Logo light height={74} className="auth-brand-logo" />
        <div className="auth-brand-copy">
          <span className="brand-kicker">{kicker}</span>
          <h1>{headline}</h1>
          <p>{promise}</p>
        </div>
        <div className="auth-brand-foot">{footer}</div>
      </section>
      <section className="auth-form-panel">
        <div className="auth-form-wrap">
          <div className="mobile-auth-hero">
            <Logo light height={34} />
            <span className="brand-kicker">{kicker}</span>
            <h1>{headline}</h1>
          </div>
          <div className="auth-card">
            {tab && (
              <div className="auth-tabs" role="tablist">
                <Link className={`auth-tab${tab === "login" ? " active" : ""}`} to={{ pathname: "/login", search: location.search }} state={location.state} role="tab" aria-selected={tab === "login"}>Iniciar sesión</Link>
                <Link className={`auth-tab${tab === "register" ? " active" : ""}`} to={{ pathname: "/registro", search: location.search }} state={location.state} role="tab" aria-selected={tab === "register"}>Crear cuenta</Link>
              </div>
            )}
            {children}
          </div>
          {tab && <Link className="auth-guest-link" to={guestPathFrom(location)}><Compass size={15} aria-hidden="true" /> Ver viajes sin crear cuenta</Link>}
        </div>
      </section>
    </main>
  );
}

function useAuthCopy(): AuthSettings {
  const gallery = useGallery();
  return { ...DEFAULT_COPY, ...(gallery.data?.auth ?? {}) };
}

export function PasswordField({ id = "password", label, value, onChange, autoComplete = "current-password", link, strength = false }: {
  id?: string;
  label: string;
  value: string;
  onChange(value: string): void;
  autoComplete?: string;
  link?: ReactNode;
  strength?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  const score = strength ? passwordScore(value) : null;
  return (
    <div className="field-block">
      <div className="label-row"><label htmlFor={id}>{label}</label>{link}</div>
      <div className="auth-input-wrap">
        <LockKeyhole size={16} aria-hidden="true" />
        <input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          autoComplete={autoComplete}
          required
          minLength={12}
          aria-describedby={score ? `${id}-strength` : undefined}
        />
        <button type="button" className="field-icon" onClick={() => setVisible((current) => !current)} aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}>
          {visible ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
        </button>
      </div>
      {score && (
        <>
          <div className={`strength-meter ${score.tone}`} aria-hidden="true">
            {[0, 1, 2, 3].map((index) => <span key={index} className={index < score.filled ? "on" : ""} />)}
          </div>
          <span className="strength-label" id={`${id}-strength`} role="status">{score.label}</span>
        </>
      )}
      {strength && <span className="field-hint">12 caracteres, mayúscula, minúscula, número y símbolo.</span>}
    </div>
  );
}

// Mirrors the API password policy so the meter never claims a rejected password is strong.
function passwordScore(value: string) {
  if (!value) return { filled: 0, tone: "weak", label: "Ingresa una contraseña" };
  const rules = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((rule) => rule.test(value)).length;
  const long = value.length >= 12;
  if (!long) return { filled: 1, tone: "weak", label: `Faltan ${12 - value.length} caracteres` };
  if (rules < 3) return { filled: 2, tone: "weak", label: "Contraseña débil" };
  if (rules < 4) return { filled: 3, tone: "medium", label: "Contraseña aceptable" };
  return { filled: 4, tone: "strong", label: "Contraseña fuerte" };
}

// Which providers are usable is a deployment question: each one needs credentials the API holds.
function useOAuthProviders() {
  const [providers, setProviders] = useState<OAuthProvider[] | null>(null);
  useEffect(() => {
    let active = true;
    apiRequest<ApiEnvelope<{ providers: OAuthProvider[] }>>("/api/v1/auth/oauth/providers", { retryAuth: false })
      .then((payload) => { if (active) setProviders(payload.data.providers); })
      .catch(() => { if (active) setProviders([]); });
    return () => { active = false; };
  }, []);
  return providers;
}

const PROVIDER_ORDER: OAuthProviderId[] = ["apple", "google"];

/**
 * Whether the "continue with" section is worth showing at all. Until a provider is configured the
 * buttons cannot do anything, and a dead control reads as a broken page. A callback error always
 * shows, so a failed sign-in is never swallowed.
 */
function shouldShowSocialAccess(providers: OAuthProvider[] | null, callbackError: string | null): boolean {
  if (callbackError) return true;
  return providers !== null && providers.length > 0;
}

function SocialAccess({ redirectTo }: { redirectTo?: string }) {
  const providers = useOAuthProviders();
  const [pending, setPending] = useState<OAuthProviderId | null>(null);
  const [error, setError] = useState<string[] | null>(null);
  const [searchParams] = useSearchParams();
  const callbackError = searchParams.get("socialError");

  if (!shouldShowSocialAccess(providers, callbackError)) return null;

  const start = async (provider: OAuthProviderId) => {
    setPending(provider); setError(null);
    try {
      const payload = await apiRequest<ApiEnvelope<{ authorizationUrl: string }>>(
        `/api/v1/auth/oauth/${provider}/start`,
        { method: "POST", body: JSON.stringify(redirectTo ? { redirectTo } : {}), retryAuth: false }
      );
      window.location.assign(payload.data.authorizationUrl);
    } catch (cause) {
      setError(describeApiError(cause, "No pudimos iniciar el acceso. Intenta de nuevo."));
      setPending(null);
    }
  };

  const available = new Map((providers ?? []).map((provider) => [provider.id, provider]));
  return (
    <div className="social-access">
      <div className="auth-divider"><span>O continúa con</span></div>
      <div className="social-grid">
        {PROVIDER_ORDER.map((id) => {
          const enabled = available.has(id);
          return (
            <button
              key={id}
              type="button"
              className="social-button"
              onClick={() => void start(id)}
              disabled={!providers || !enabled || pending !== null}
              aria-describedby={providers && !enabled ? "social-status" : undefined}
            >
              {pending === id
                ? <Spinner />
                : id === "apple" ? <Apple size={16} aria-hidden="true" /> : <GoogleMark />}
              {id === "apple" ? "Apple" : "Google"}
            </button>
          );
        })}
      </div>
      <FormError messages={error ?? (callbackError ? [describeSocialError(callbackError)] : null)} />
      {providers && providers.length > 0 && providers.length < PROVIDER_ORDER.length && (
        <span className="field-hint" id="social-status">
          {PROVIDER_ORDER.filter((id) => !available.has(id)).map((id) => (id === "apple" ? "Apple" : "Google")).join(" y ")}
          {" aún no está disponible."}
        </span>
      )}
    </div>
  );
}

// The callback can only hand back a code, since a message in a redirect URL is attacker-controlled.
function describeSocialError(code: string): string {
  switch (code) {
    case "ACCESS_DENIED":
      return "Cancelaste el acceso con el proveedor.";
    case "EMAIL_REQUIRED":
      return "Tu proveedor no compartió un correo electrónico, que necesitamos para tu cuenta.";
    case "EMAIL_NOT_VERIFIED_BY_PROVIDER":
      return "Ya existe una cuenta con ese correo. Ingresa con tu contraseña para vincularla.";
    case "ACCOUNT_INACTIVE":
      return "Tu cuenta no está activa. Contacta a soporte.";
    case "STATE_INVALID":
      return "El intento de acceso expiró. Vuelve a intentarlo.";
    default:
      return "No pudimos completar el acceso con el proveedor. Intenta de nuevo.";
  }
}

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 35 26.8 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.5 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C41.4 35.4 44 30.1 44 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}

export function EmailField({ id = "email", value, onChange }: { id?: string; value: string; onChange(value: string): void }) {
  return (
    <div className="field-block">
      <label htmlFor={id}>Email</label>
      <div className="auth-input-wrap">
        <Mail size={16} aria-hidden="true" />
        <input id={id} type="email" value={value} onChange={(event) => onChange(event.target.value)} autoComplete="email" required placeholder="tu@correo.com" />
      </div>
    </div>
  );
}

export function LoginPage() {
  const { user, login } = useAuth();
  const copy = useAuthCopy();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string[] | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  if (user) return <Navigate to={user.role === "lender" ? "/aliados" : returnPathFrom(location) ?? "/inicio"} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true); setError(null);
    try {
      await login(email, password);
      navigate(returnPathFrom(location) ?? "/inicio", { replace: true });
    } catch (cause) {
      setError(describeApiError(cause, "No pudimos iniciar sesión."));
    } finally { setSubmitting(false); }
  };

  return (
    <AuthLayout tab="login" kicker="Bienvenido" headline={copy.loginHeadline} promise={copy.loginPromise}>
      <form className="auth-form" onSubmit={submit}>
        <EmailField value={email} onChange={setEmail} />
        <PasswordField
          label="Contraseña"
          value={password}
          onChange={setPassword}
          link={<Link to="/recuperar">¿La olvidaste?</Link>}
        />
        <FormError messages={error} />
        <button className="primary-button full" type="submit" disabled={submitting}>
          <ButtonLabel busy={submitting} busyLabel="Entrando" label="Entrar" icon={<ArrowRight size={16} aria-hidden="true" />} />
        </button>
      </form>
      {import.meta.env.DEV && (
        <button className="demo-access" type="button" onClick={() => { setEmail("demo@aventuria.mx"); setPassword("Aventura-Segura-2026!"); }}>
          <span>Acceso local</span><strong>demo@aventuria.mx</strong>
        </button>
      )}
      <SocialAccess redirectTo={returnPathFrom(location)} />
      <p className="auth-footnote">
        Al continuar aceptas los <a href="/terminos-y-condiciones" target="_blank" rel="noreferrer">Términos y condiciones</a> y el <a href="/aviso-de-privacidad" target="_blank" rel="noreferrer">Aviso de Privacidad</a>.
      </p>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const { user, register } = useAuth();
  const copy = useAuthCopy();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string[] | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  if (user) return <Navigate to={returnPathFrom(location) ?? "/inicio"} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError(null);
    if (!accepted) { setError(["Acepta los términos para continuar."]); return; }
    setSubmitting(true);
    try { await register(fullName, email, password); navigate(returnPathFrom(location) ?? "/inicio", { replace: true }); }
    catch (cause) { setError(describeApiError(cause, "No pudimos crear tu cuenta.")); }
    finally { setSubmitting(false); }
  };

  return (
    <AuthLayout tab="register" kicker="Bienvenido" headline={copy.signupHeadline} promise={copy.signupPromise}>
      <form className="auth-form" onSubmit={submit}>
        <div className="field-block">
          <label htmlFor="full-name">Nombre</label>
          <div className="auth-input-wrap">
            <UserRound size={16} aria-hidden="true" />
            <input id="full-name" value={fullName} onChange={(event) => setFullName(event.target.value)} autoComplete="name" required placeholder="Nombre y apellido" />
          </div>
        </div>
        <EmailField value={email} onChange={setEmail} />
        <PasswordField label="Crea contraseña" value={password} onChange={setPassword} autoComplete="new-password" strength />
        <label className="terms-row">
          <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
          <span className="terms-box" aria-hidden="true"><Check size={12} strokeWidth={3} /></span>
          <span>Acepto los <a href="/terminos-y-condiciones" target="_blank" rel="noreferrer">Términos y condiciones</a> y autorizo el tratamiento de mis datos conforme al <a href="/aviso-de-privacidad" target="_blank" rel="noreferrer">Aviso de Privacidad</a>.</span>
        </label>
        <p className="auth-privacy-brief">{SIMPLIFIED_PRIVACY_NOTICE}</p>
        <FormError messages={error} />
        <button className="primary-button full" type="submit" disabled={submitting}>
          <ButtonLabel busy={submitting} busyLabel="Creando cuenta" label="Crear cuenta" icon={<ArrowRight size={16} aria-hidden="true" />} />
        </button>
      </form>
      <SocialAccess redirectTo={returnPathFrom(location)} />
      <p className="auth-switch">¿Ya tienes cuenta? <Link to={{ pathname: "/login", search: location.search }} state={location.state}>Inicia sesión</Link></p>
    </AuthLayout>
  );
}

export function RecoveryPage() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [developmentToken, setDevelopmentToken] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setSubmitting(true);
    try {
      const result = await apiRequest<ApiEnvelope<{ message: string; developmentResetToken?: string }>>("/api/v1/auth/password-reset/request", {
        method: "POST", body: JSON.stringify({ email }), retryAuth: false
      });
      setMessage(result.data.message);
      setDevelopmentToken(result.data.developmentResetToken ?? null);
    } catch (cause) { setMessage(cause instanceof Error ? cause.message : "No pudimos procesar la solicitud."); }
    finally { setSubmitting(false); }
  };
  return (
    <AuthLayout
      kicker="Recuperar acceso"
      headline="Volvamos a tu próxima aventura."
      promise="Te enviamos un enlace temporal para que puedas crear una contraseña nueva."
    >
      <div className="auth-heading"><h2>Recupera tu acceso</h2><p>Te enviaremos las instrucciones a tu correo.</p></div>
      <form className="auth-form" onSubmit={submit}>
        <EmailField id="recovery-email" value={email} onChange={setEmail} />
        {message && <div className="form-message" role="status">{message}</div>}
        {developmentToken && <Link className="secondary-button full" to={`/restablecer?token=${encodeURIComponent(developmentToken)}`}>Continuar en entorno local</Link>}
        <button className="primary-button full" type="submit" disabled={submitting}>
          <ButtonLabel busy={submitting} busyLabel="Enviando" label="Enviar instrucciones" icon={<ArrowRight size={16} aria-hidden="true" />} />
        </button>
      </form>
      <Link className="back-link" to="/login"><ArrowLeft size={16} aria-hidden="true" /> Volver a ingresar</Link>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string[] | null>(null);
  const token = searchParams.get("token") ?? "";
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError(null);
    if (!token) { setError(["El enlace de recuperación no es válido."]); return; }
    if (password !== confirmation) { setError(["Las contraseñas no coinciden."]); return; }
    setSubmitting(true);
    try {
      await apiRequest<ApiEnvelope<{ message: string }>>("/api/v1/auth/password-reset/confirm", {
        method: "POST", body: JSON.stringify({ token, password }), retryAuth: false
      });
      navigate("/login", { replace: true });
    } catch (cause) { setError(describeApiError(cause, "No pudimos actualizar tu contraseña.")); }
    finally { setSubmitting(false); }
  };
  return (
    <AuthLayout
      kicker="Nueva contraseña"
      headline="Protege tu cuenta con una clave nueva."
      promise="El cambio cierra las sesiones activas en todos tus dispositivos."
    >
      <div className="auth-heading"><h2>Crea una nueva contraseña</h2><p>El cambio cerrará tus sesiones activas.</p></div>
      <form className="auth-form" onSubmit={submit}>
        <PasswordField label="Nueva contraseña" value={password} onChange={setPassword} autoComplete="new-password" strength />
        <div className="field-block">
          <label htmlFor="password-confirmation">Confirma la contraseña</label>
          <div className="auth-input-wrap">
            <LockKeyhole size={16} aria-hidden="true" />
            <input id="password-confirmation" type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" required minLength={12} />
          </div>
        </div>
        <FormError messages={error} />
        <button className="primary-button full" type="submit" disabled={submitting}>
          <ButtonLabel busy={submitting} busyLabel="Actualizando" label="Actualizar contraseña" icon={<ArrowRight size={16} aria-hidden="true" />} />
        </button>
      </form>
    </AuthLayout>
  );
}

/**
 * Landing point after a provider round trip. The API has already set the refresh cookie, so this
 * only has to convert it into a live session — the same exchange a returning visitor performs.
 */
export function OAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const status = searchParams.get("status");
  const code = searchParams.get("code");
  const redirectTo = safeReturnPath(searchParams.get("redirectTo"));

  useEffect(() => {
    if (status !== "ok") {
      navigate(`/login?socialError=${encodeURIComponent(code ?? "PROVIDER_ERROR")}`, { replace: true });
      return;
    }
    let active = true;
    void refresh().then((signedIn) => {
      if (!active) return;
      navigate(signedIn ? (redirectTo ?? "/inicio") : "/login?socialError=SESSION_FAILED", { replace: true });
    });
    return () => { active = false; };
  }, [status, code, redirectTo, refresh, navigate]);

  return <BrandSplash label={status === "ok" ? "Completando tu acceso a tu próxima" : "Volviendo al inicio de"} headline={status === "ok" ? "aventura." : "sesión."} />;
}

export const authHelpers = { passwordScore, describeSocialError, shouldShowSocialAccess };
